import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { childrenAPI, badgesAPI, avatarAPI } from '../api';
import { toast } from 'sonner';
import { Plus, User, ArrowLeft, Pencil, Sparkles, Award, Wand2, Lock, X, Settings, HelpCircle, Info } from 'lucide-react';
import { AvatarPreview, AvatarBuilderModal } from '../components/AvatarBuilder';
import { FullBodyAvatarPreview } from '../components/FullBodyAvatar';

// Onboarding tooltip component - positioned above cards for visibility
const OnboardingTooltip = ({ show, onClose, children }) => {
  if (!show) return null;
  
  return (
    <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] max-w-sm mx-4">
      <div className="bg-indigo-600 text-white px-5 py-4 rounded-xl shadow-2xl border border-indigo-400/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            {children}
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Arrow pointing down */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-indigo-600 rotate-45" />
    </div>
  );
};

// Life Path Number traits and descriptions
const LIFE_PATH_TRAITS = {
  1: {
    title: "The Leader",
    traits: ["Independent", "Creative", "Pioneering", "Self-motivated"],
    strengths: "Natural leadership abilities, original thinking, determination",
    learning: "Thrives with independent projects and self-directed learning",
    interests: "Entrepreneurship, invention, individual sports, leadership roles"
  },
  2: {
    title: "The Peacemaker",
    traits: ["Cooperative", "Sensitive", "Diplomatic", "Patient"],
    strengths: "Harmony-building, empathy, attention to detail, partnership",
    learning: "Excels in collaborative environments and group projects",
    interests: "Music, counseling, teamwork activities, artistic expression"
  },
  3: {
    title: "The Communicator",
    traits: ["Expressive", "Creative", "Optimistic", "Social"],
    strengths: "Verbal and written expression, artistic talent, inspiration",
    learning: "Learns best through storytelling, art, and verbal exchange",
    interests: "Writing, performing arts, public speaking, creative crafts"
  },
  4: {
    title: "The Builder",
    traits: ["Practical", "Reliable", "Organized", "Hard-working"],
    strengths: "Systems thinking, discipline, creating stable foundations",
    learning: "Benefits from structured routines and hands-on activities",
    interests: "Engineering, building, organizing, nature and gardening"
  },
  5: {
    title: "The Explorer",
    traits: ["Adventurous", "Versatile", "Curious", "Freedom-loving"],
    strengths: "Adaptability, enthusiasm, resourcefulness, communication",
    learning: "Needs variety and movement; learns through experience",
    interests: "Travel, languages, physical activities, variety of subjects"
  },
  6: {
    title: "The Nurturer",
    traits: ["Caring", "Responsible", "Protective", "Harmonious"],
    strengths: "Compassion, family focus, artistic vision, healing abilities",
    learning: "Flourishes when caring for others or creative projects",
    interests: "Caregiving, home arts, music, community service"
  },
  7: {
    title: "The Seeker",
    traits: ["Analytical", "Intuitive", "Reflective", "Knowledge-seeking"],
    strengths: "Deep thinking, research skills, spiritual awareness",
    learning: "Prefers quiet study, mysteries, and deep-dive topics",
    interests: "Science, philosophy, research, nature, meditation"
  },
  8: {
    title: "The Achiever",
    traits: ["Ambitious", "Goal-oriented", "Authoritative", "Efficient"],
    strengths: "Business sense, material success, organization, leadership",
    learning: "Motivated by goals, rewards, and measurable progress",
    interests: "Business, strategy games, sports, achievement-based activities"
  },
  9: {
    title: "The Humanitarian",
    traits: ["Compassionate", "Generous", "Creative", "Wise"],
    strengths: "Universal understanding, artistic talent, selflessness",
    learning: "Inspired by helping others and creative expression",
    interests: "Arts, philanthropy, global cultures, healing arts"
  }
};

const AVATAR_COLORS = [
  '#818CF8', '#4ADE80', '#FBBF24', '#60A5FA', '#FB923C', 
  '#2DD4BF', '#F472B6', '#A78BFA', '#34D399', '#FCD34D'
];

const ChildSelectPage = () => {
  const [children, setChildren] = useState([]);
  const [childBadges, setChildBadges] = useState({}); // {childId: [badges]}
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [newChildName, setNewChildName] = useState('');
  const [newChildBirthdate, setNewChildBirthdate] = useState('');
  const [selectedColor, setSelectedColor] = useState(AVATAR_COLORS[0]);
  // Avatar builder state
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);
  const [avatarBuilderId, setAvatarBuilderId] = useState(null);
  const [avatarBuilderInitial, setAvatarBuilderInitial] = useState(null);
  // PIN verification state
  const [showPinModal, setShowPinModal] = useState(false);
  
  // Onboarding hints state
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPinHint, setShowPinHint] = useState(() => {
    return localStorage.getItem('toc_seen_pin_hint') !== 'true';
  });
  const [showLifePathModal, setShowLifePathModal] = useState(false);
  const [selectedLifePath, setSelectedLifePath] = useState(null);
  const [pinChild, setPinChild] = useState(null);
  const [pinInput, setPinInput] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  // PIN setup state
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pinSetupChild, setPinSetupChild] = useState(null);
  const [newPin, setNewPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [pinSetupError, setPinSetupError] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const res = await childrenAPI.getAll();
      setChildren(res.data);
      
      // Fetch badges for each child
      const badgesMap = {};
      for (const child of res.data) {
        try {
          const badgesRes = await badgesAPI.getChildBadges(child.id);
          badgesMap[child.id] = badgesRes.data || [];
        } catch (e) {
          badgesMap[child.id] = [];
        }
      }
      setChildBadges(badgesMap);
    } catch (error) {
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await childrenAPI.create({
        name: newChildName,
        birthdate: newChildBirthdate || null,
        avatar_color: selectedColor,
      });
      toast.success('Child profile created!');
      setNewChildName('');
      setNewChildBirthdate('');
      setShowAdd(false);
      fetchChildren();
    } catch (error) {
      toast.error('Failed to create child profile');
    }
  };

  const handleEditChild = (child, e) => {
    e.stopPropagation(); // Prevent selecting the child
    setEditingChild(child);
    setNewChildName(child.name);
    setNewChildBirthdate(child.birthdate || '');
    setSelectedColor(child.avatar_color);
    setShowEdit(true);
  };

  const handleUpdateChild = async (e) => {
    e.preventDefault();
    try {
      const res = await childrenAPI.update(editingChild.id, {
        name: newChildName,
        birthdate: newChildBirthdate || null,
        avatar_color: selectedColor,
      });
      toast.success('Child profile updated!');
      
      // Update localStorage if this child is currently selected
      const selectedChild = localStorage.getItem('selectedChild');
      if (selectedChild) {
        const parsed = JSON.parse(selectedChild);
        if (parsed.id === editingChild.id) {
          localStorage.setItem('selectedChild', JSON.stringify(res.data));
        }
      }
      
      setShowEdit(false);
      setEditingChild(null);
      setNewChildName('');
      setNewChildBirthdate('');
      fetchChildren();
    } catch (error) {
      toast.error('Failed to update child profile');
    }
  };

  const closeEditModal = () => {
    setShowEdit(false);
    setEditingChild(null);
    setNewChildName('');
    setNewChildBirthdate('');
    setSelectedColor(AVATAR_COLORS[0]);
  };

  const handleSelectChild = async (child) => {
    // If child has a PIN set, show PIN verification modal
    if (child.pin) {
      setPinChild(child);
      setPinInput(['', '', '', '']);
      setPinError('');
      setShowPinModal(true);
    } else {
      // No PIN required, go directly to temple
      localStorage.setItem('selectedChild', JSON.stringify(child));
      navigate('/temple');
    }
  };

  // PIN input handler
  const handlePinInput = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newPinInput = [...pinInput];
    newPinInput[index] = value.slice(-1); // Only keep last digit
    setPinInput(newPinInput);
    
    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-input-${index + 1}`);
      nextInput?.focus();
    }
    
    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newPinInput.every(d => d !== '')) {
      handleVerifyPin(newPinInput);
    }
  };

  const handleVerifyPin = async (pinDigits = pinInput) => {
    if (pinDigits.some(d => d === '')) return;
    
    const pin = pinDigits.join('');
    setVerifyingPin(true);
    setPinError('');
    
    try {
      const res = await childrenAPI.verifyPin(pinChild.id, pin);
      if (res.data.verified) {
        localStorage.setItem('selectedChild', JSON.stringify(pinChild));
        setShowPinModal(false);
        navigate('/temple');
      } else {
        setPinError('Incorrect PIN. Try again!');
        setPinInput(['', '', '', '']);
        document.getElementById('pin-input-0')?.focus();
      }
    } catch (error) {
      setPinError('Something went wrong. Try again!');
    } finally {
      setVerifyingPin(false);
    }
  };

  // PIN Setup handlers
  const handleOpenPinSetup = (child, e) => {
    e.stopPropagation();
    setPinSetupChild(child);
    setNewPin(['', '', '', '']);
    setConfirmPin(['', '', '', '']);
    setPinSetupError('');
    setShowPinSetup(true);
  };

  const handlePinSetupInput = (index, value, isConfirm = false) => {
    if (!/^\d*$/.test(value)) return;
    
    const setter = isConfirm ? setConfirmPin : setNewPin;
    const currentPin = isConfirm ? confirmPin : newPin;
    
    const newPinInput = [...currentPin];
    newPinInput[index] = value.slice(-1);
    setter(newPinInput);
    
    // Auto-focus next input
    if (value && index < 3) {
      const prefix = isConfirm ? 'confirm' : 'new';
      const nextInput = document.getElementById(`${prefix}-pin-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleSavePin = async () => {
    const pin1 = newPin.join('');
    const pin2 = confirmPin.join('');
    
    if (pin1.length !== 4) {
      setPinSetupError('Please enter a 4-digit PIN');
      return;
    }
    
    if (pin1 !== pin2) {
      setPinSetupError('PINs do not match. Try again!');
      setConfirmPin(['', '', '', '']);
      return;
    }
    
    try {
      await childrenAPI.setPin(pinSetupChild.id, pin1);
      
      // Update local state
      setChildren(prev => prev.map(c => 
        c.id === pinSetupChild.id ? { ...c, pin: pin1 } : c
      ));
      
      toast.success('PIN set successfully!');
      setShowPinSetup(false);
      setPinSetupChild(null);
    } catch (error) {
      setPinSetupError('Failed to save PIN. Try again!');
    }
  };

  const handleRemovePin = async () => {
    try {
      await childrenAPI.setPin(pinSetupChild.id, '');
      
      // Update local state
      setChildren(prev => prev.map(c => 
        c.id === pinSetupChild.id ? { ...c, pin: null } : c
      ));
      
      toast.success('PIN removed successfully!');
      setShowPinSetup(false);
      setPinSetupChild(null);
    } catch (error) {
      toast.error('Failed to remove PIN');
    }
  };

  // Avatar builder handlers
  const handleOpenAvatarBuilder = (child, e) => {
    e.stopPropagation(); // Prevent selecting the child
    setAvatarBuilderId(child.id);
    setAvatarBuilderInitial(child.avatar || null);
    setShowAvatarBuilder(true);
  };
  
  const handleAvatarSave = (newAvatar) => {
    // Update the child in local state to show the new avatar immediately
    setChildren(prev => prev.map(c => 
      c.id === avatarBuilderId 
        ? { ...c, avatar: newAvatar }
        : c
    ));
    
    // Also update localStorage if this child is currently selected
    const selectedChild = localStorage.getItem('selectedChild');
    if (selectedChild) {
      const parsed = JSON.parse(selectedChild);
      if (parsed.id === avatarBuilderId) {
        localStorage.setItem('selectedChild', JSON.stringify({ ...parsed, avatar: newAvatar }));
      }
    }
    
    setShowAvatarBuilder(false);
    setAvatarBuilderId(null);
    setAvatarBuilderInitial(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] px-4 sm:px-6 py-6 sm:py-12">
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <button
          data-testid="back-to-dashboard-btn"
          onClick={() => navigate('/dashboard')}
          className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 z-10 relative"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </button>
        
        {/* Help Button */}
        <button
          onClick={() => setShowHelpModal(true)}
          className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
          title="How to use"
          data-testid="help-btn"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-philosopher font-bold text-center mb-3 sm:mb-4 glow-text">
          Who's Learning Today?
        </h1>
        <p className="text-slate-400 text-center text-sm sm:text-base mb-8 sm:mb-12">Select a child profile to enter the temple</p>

        {/* Floating PIN Hint - shows on first visit */}
        {showPinHint && children.length > 0 && (
          <OnboardingTooltip 
            show={true} 
            onClose={() => {
              setShowPinHint(false);
              localStorage.setItem('toc_seen_pin_hint', 'true');
            }}
          >
            <p className="text-sm font-medium mb-1">🔒 PIN Protection</p>
            <p className="text-xs text-white/80">Look for the lock icon on each child's card. Click it to set a 4-digit PIN that protects their profile.</p>
          </OnboardingTooltip>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {children.map((child, index) => {
            const hasAvatar = child.avatar && Object.keys(child.avatar).length > 0;
            
            return (
              <div
                key={child.id}
                className="relative glass-panel p-4 sm:p-8 rounded-xl sm:rounded-2xl hover:border-white/30 transition-all duration-300 hover:scale-105 text-center cursor-pointer"
                data-testid={`child-profile-${child.id}`}
                onClick={() => handleSelectChild(child)}
              >
                {/* Edit Button */}
                <button
                  onClick={(e) => handleEditChild(child, e)}
                  className="absolute top-3 sm:top-4 right-3 sm:right-4 p-1.5 sm:p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
                  data-testid={`edit-child-${child.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                
                {/* PIN Settings Button - clearly positioned top-left */}
                <button
                  onClick={(e) => handleOpenPinSetup(child, e)}
                  className={`absolute top-3 sm:top-4 left-3 sm:left-4 p-1.5 sm:p-2 rounded-full transition-all z-10 ${
                    child.pin 
                      ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 bg-amber-500/10' 
                      : 'text-slate-500 hover:text-slate-300 hover:bg-white/10'
                  }`}
                  title={child.pin ? 'PIN Protected - Click to manage' : 'Set PIN to protect this profile'}
                  data-testid={`pin-settings-${child.id}`}
                >
                  <Lock className="w-4 h-4" />
                </button>
                
                {/* Avatar Section - Full Body */}
                <div className="relative mx-auto mb-3 sm:mb-4" style={{ width: 120, height: 180 }}>
                  {hasAvatar ? (
                    <FullBodyAvatarPreview avatar={child.avatar} size="lg" type="explorer" isIdle={true} />
                  ) : (
                    // Default placeholder when no avatar exists
                    <div
                      className="w-24 h-24 mx-auto mt-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: child.avatar_color || '#818CF8' }}
                    >
                      <User className="w-12 h-12 text-white" />
                    </div>
                  )}
                  
                  {/* Create/Edit Avatar Button */}
                  <button
                    onClick={(e) => handleOpenAvatarBuilder(child, e)}
                    className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                      hasAvatar 
                        ? 'bg-slate-700/80 hover:bg-slate-600/80 text-slate-200' 
                        : 'bg-indigo-500/90 hover:bg-indigo-400/90 text-white animate-pulse'
                    }`}
                    data-testid={`avatar-btn-${child.id}`}
                  >
                    <Wand2 className="w-3 h-3" />
                    {hasAvatar ? 'Edit' : 'Create Explorer'}
                  </button>
                </div>
                
                <h3 className="text-xl font-philosopher font-bold text-white mt-4">{child.name}</h3>
                {child.life_path && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLifePath(child.life_path);
                      setShowLifePathModal(true);
                    }}
                    className="text-cyan-400 text-sm mt-1 hover:text-cyan-300 hover:underline flex items-center gap-1 mx-auto transition-colors"
                    data-testid={`life-path-${child.id}`}
                  >
                    Life Path {child.life_path}
                    <Info className="w-3 h-3" />
                  </button>
                )}
                {child.sparks && (
                  <div className="flex items-center justify-center gap-1 text-yellow-400 text-sm mt-2">
                    <Sparkles className="w-4 h-4" />
                    <span>{Object.values(child.sparks || {}).reduce((a, b) => a + b, 0)} sparks</span>
                  </div>
                )}
                {/* Badges */}
                {childBadges[child.id]?.length > 0 && (
                  <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
                    {childBadges[child.id].slice(0, 4).map((cb, i) => (
                      <div
                        key={i}
                        className="w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: cb.badge?.color || '#FFD700' }}
                        title={cb.badge?.name}
                      >
                        <Award className="w-4 h-4 text-white" />
                      </div>
                    ))}
                    {childBadges[child.id].length > 4 && (
                      <span className="text-slate-400 text-xs">+{childBadges[child.id].length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add New Child Button */}
          <button
            data-testid="add-child-btn"
            onClick={() => setShowAdd(true)}
            className="glass-panel p-8 rounded-2xl border-dashed hover:border-white/30 transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center min-h-[200px]"
          >
            <Plus className="w-12 h-12 text-indigo-400 mb-2" />
            <span className="text-slate-300 font-semibold">Add Child</span>
          </button>
        </div>
      </div>

      {/* Add Child Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-panel rounded-3xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-philosopher font-bold mb-6 text-white">Add Child Profile</h2>
            <form onSubmit={handleAddChild} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Name</label>
                <input
                  data-testid="child-name-input"
                  type="text"
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                  placeholder="Child's name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">
                  Birthdate <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  data-testid="child-birthdate-input"
                  type="date"
                  value={newChildBirthdate}
                  onChange={(e) => setNewChildBirthdate(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                />
                <div className="mt-2 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                  <p className="text-xs text-slate-400">
                    <span className="text-indigo-400 font-medium">Life Path Number:</span> A numerology-based number calculated from birthdate. 
                    Some families enjoy using it as a fun personalization tool for portal recommendations.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Completely optional — you can skip this and still enjoy the full Temple experience!
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Avatar Color</label>
                <div className="grid grid-cols-5 gap-3">
                  {AVATAR_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSelectedColor(color)}
                      className={`w-12 h-12 rounded-full transition-all ${
                        selectedColor === color ? 'ring-4 ring-white/50 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="cancel-add-child-btn"
                  onClick={() => setShowAdd(false)}
                  className="flex-1 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-full px-6 py-3 transition-all"
                >
                  Cancel
                </button>
                <button
                  data-testid="save-child-btn"
                  type="submit"
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Child Modal */}
      {showEdit && editingChild && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-panel rounded-3xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-philosopher font-bold mb-6 text-white">Edit Child Profile</h2>
            <form onSubmit={handleUpdateChild} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Name</label>
                <input
                  data-testid="edit-child-name-input"
                  type="text"
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                  placeholder="Child's name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">
                  Birthdate <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  data-testid="edit-child-birthdate-input"
                  type="date"
                  value={newChildBirthdate}
                  onChange={(e) => setNewChildBirthdate(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                />
                {editingChild.birthdate !== newChildBirthdate && newChildBirthdate && (
                  <p className="text-xs text-amber-400 mt-1">
                    Changing birthdate will recalculate the Life Path Number
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Avatar Color</label>
                <div className="grid grid-cols-5 gap-3">
                  {AVATAR_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSelectedColor(color)}
                      className={`w-12 h-12 rounded-full transition-all ${
                        selectedColor === color ? 'ring-4 ring-white/50 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {editingChild.life_path && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                  <p className="text-cyan-400 text-sm">
                    Life Path Number: <span className="font-bold">{editingChild.life_path}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Calculated from birthdate. Used for personalized portal recommendations.
                  </p>
                </div>
              )}
              
              {!editingChild.life_path && !newChildBirthdate && (
                <div className="bg-slate-500/10 border border-slate-500/20 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">
                    No Life Path Number set — add a birthdate above if you'd like personalized recommendations.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="cancel-edit-child-btn"
                  onClick={closeEditModal}
                  className="flex-1 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-full px-6 py-3 transition-all"
                >
                  Cancel
                </button>
                <button
                  data-testid="update-child-btn"
                  type="submit"
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Avatar Builder Modal */}
      <AvatarBuilderModal
        isOpen={showAvatarBuilder}
        onClose={() => {
          setShowAvatarBuilder(false);
          setAvatarBuilderId(null);
          setAvatarBuilderInitial(null);
        }}
        childId={avatarBuilderId}
        initialAvatar={avatarBuilderInitial}
        onSave={handleAvatarSave}
      />

      {/* PIN Verification Modal */}
      {showPinModal && pinChild && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-8 rounded-2xl max-w-sm w-full text-center">
            <button
              onClick={() => setShowPinModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <Lock className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-philosopher font-bold text-white mb-2">
              Welcome, {pinChild.name}!
            </h2>
            <p className="text-slate-400 mb-6">Enter your 4-digit PIN to continue</p>
            
            {/* PIN Input */}
            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  id={`pin-input-${index}`}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={pinInput[index]}
                  onChange={(e) => handlePinInput(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !pinInput[index] && index > 0) {
                      document.getElementById(`pin-input-${index - 1}`)?.focus();
                    }
                  }}
                  className={`w-14 h-14 text-center text-2xl font-bold rounded-xl bg-slate-800/50 border-2 ${
                    pinError ? 'border-red-500' : 'border-slate-600 focus:border-indigo-500'
                  } text-white outline-none transition-all`}
                  autoFocus={index === 0}
                  data-testid={`pin-digit-${index}`}
                />
              ))}
            </div>
            
            {pinError && (
              <p className="text-red-400 text-sm mb-4 animate-shake">{pinError}</p>
            )}
            
            <button
              onClick={() => handleVerifyPin()}
              disabled={verifyingPin || pinInput.some(d => d === '')}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold disabled:opacity-50 transition-all hover:opacity-90"
              data-testid="verify-pin-btn"
            >
              {verifyingPin ? 'Checking...' : 'Enter Temple'}
            </button>
            
            <p className="text-slate-500 text-xs mt-4">
              Forgot your PIN? Ask a parent to help!
            </p>
          </div>
        </div>
      )}

      {/* PIN Setup Modal */}
      {showPinSetup && pinSetupChild && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-8 rounded-2xl max-w-sm w-full text-center relative">
            <button
              onClick={() => setShowPinSetup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Settings className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-xl font-philosopher font-bold text-white mb-2">
              Set PIN for {pinSetupChild.name}
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              Create a 4-digit PIN to protect this profile
            </p>
            
            {/* New PIN */}
            <div className="mb-4">
              <p className="text-slate-300 text-sm mb-2">Enter new PIN:</p>
              <div className="flex justify-center gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    id={`new-pin-input-${index}`}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={newPin[index]}
                    onChange={(e) => handlePinSetupInput(index, e.target.value, false)}
                    className="w-12 h-12 text-center text-xl font-bold rounded-lg bg-slate-800/50 border-2 border-slate-600 focus:border-amber-500 text-white outline-none transition-all"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>
            
            {/* Confirm PIN */}
            <div className="mb-4">
              <p className="text-slate-300 text-sm mb-2">Confirm PIN:</p>
              <div className="flex justify-center gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    id={`confirm-pin-input-${index}`}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={confirmPin[index]}
                    onChange={(e) => handlePinSetupInput(index, e.target.value, true)}
                    className="w-12 h-12 text-center text-xl font-bold rounded-lg bg-slate-800/50 border-2 border-slate-600 focus:border-amber-500 text-white outline-none transition-all"
                  />
                ))}
              </div>
            </div>
            
            {pinSetupError && (
              <p className="text-red-400 text-sm mb-4">{pinSetupError}</p>
            )}
            
            <div className="flex gap-3">
              {pinSetupChild.pin && (
                <button
                  onClick={handleRemovePin}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-full px-4 py-2 text-sm transition-all"
                >
                  Remove PIN
                </button>
              )}
              <button
                onClick={handleSavePin}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full px-4 py-2 font-semibold transition-all hover:opacity-90"
                data-testid="save-pin-btn"
              >
                Save PIN
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-6 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-indigo-400" />
                How to Use Temple of Children
              </h3>
              <button onClick={() => setShowHelpModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* PIN Protection */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-amber-400" />
                  </div>
                  <h4 className="text-white font-semibold">PIN Protection</h4>
                </div>
                <p className="text-slate-400 text-sm">Click the <Lock className="w-3 h-3 inline text-amber-400" /> icon on any child profile to set a 4-digit PIN. This keeps their profile secure.</p>
              </div>
              
              {/* Sparks */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h4 className="text-white font-semibold">Sparks</h4>
                </div>
                <p className="text-slate-400 text-sm">Sparks are earned by completing lessons and activities. They show progress and unlock achievements!</p>
              </div>
              
              {/* Avatar */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Wand2 className="w-4 h-4 text-purple-400" />
                  </div>
                  <h4 className="text-white font-semibold">Custom Avatars</h4>
                </div>
                <p className="text-slate-400 text-sm">Each child can create their own unique avatar! Click "Create Avatar" or "Edit" under their profile picture.</p>
              </div>
              
              {/* Navigation */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Info className="w-4 h-4 text-cyan-400" />
                  </div>
                  <h4 className="text-white font-semibold">Getting Started</h4>
                </div>
                <p className="text-slate-400 text-sm">
                  1. Select a child profile to enter the Temple<br/>
                  2. Choose a Journey to begin learning<br/>
                  3. Complete lessons to light up stars in Temple Sky<br/>
                  4. Explore the Courtyard for fun activities!
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-6 w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl px-4 py-3 font-semibold hover:opacity-90 transition-all"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
      
      {/* Life Path Explanation Modal */}
      {showLifePathModal && selectedLifePath && LIFE_PATH_TRAITS[selectedLifePath] && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-panel p-6 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-cyan-400">{selectedLifePath}</span>
                </div>
                <div>
                  <h3 className="text-xl font-philosopher font-bold text-white">
                    Life Path {selectedLifePath}
                  </h3>
                  <p className="text-cyan-400">{LIFE_PATH_TRAITS[selectedLifePath].title}</p>
                </div>
              </div>
              <button onClick={() => setShowLifePathModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Key Traits */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Key Personality Traits
                </h4>
                <div className="flex flex-wrap gap-2">
                  {LIFE_PATH_TRAITS[selectedLifePath].traits.map((trait, i) => (
                    <span key={i} className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">
                      {trait}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Natural Strengths */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-400" />
                  Natural Strengths
                </h4>
                <p className="text-slate-300 text-sm">{LIFE_PATH_TRAITS[selectedLifePath].strengths}</p>
              </div>
              
              {/* Learning Style */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-purple-400" />
                  Learning Style
                </h4>
                <p className="text-slate-300 text-sm">{LIFE_PATH_TRAITS[selectedLifePath].learning}</p>
              </div>
              
              {/* Interests */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Possible Interests
                </h4>
                <p className="text-slate-300 text-sm">{LIFE_PATH_TRAITS[selectedLifePath].interests}</p>
              </div>
            </div>
            
            <p className="text-slate-500 text-xs mt-4 text-center">
              Life Path numbers are based on numerology and offer one perspective on personality traits.
              Every child is unique!
            </p>
            
            <button
              onClick={() => setShowLifePathModal(false)}
              className="mt-4 w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl px-4 py-3 font-semibold hover:opacity-90 transition-all"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildSelectPage;
